/**
 * @file   src/components/AiChat/AiChatPanel.js
 * @brief  Main AI chat container for want ad creation
 * @date   February, 2026
 * @author Jake Hokinson
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import AiChatMessage from "./AiChatMessage";
import AiChatInput from "./AiChatInput";
import AiWantAdPreview from "./AiWantAdPreview";
import { generateWantAd } from "../../services/aiChatService";

const GREETING_MESSAGE = {
  role: "assistant",
  content:
    "Tell me about the property your client is looking for. Where, what type (home, land, condo, etc.), budget, and what matters most to them. The more you share, the better I can help.",
};

const AiChatPanel = ({ onWantAdGenerated }) => {
  const [messages, setMessages] = useState([GREETING_MESSAGE]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatedData, setGeneratedData] = useState(null);
  const [fairHousingStatus, setFairHousingStatus] = useState(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, error]);

  const handleSend = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isLoading) return;

    const userMessage = { role: "user", content: trimmed };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputValue("");
    setError(null);
    setIsLoading(true);

    // Only send user/assistant messages (not the hardcoded greeting) to the API
    const apiMessages = updatedMessages.filter(
      (m) => m !== GREETING_MESSAGE
    );

    // 30-second timeout
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      setError("The request timed out. Please try again.");
    }, 30000);

    try {
      const result = await generateWantAd(apiMessages, "BUY");

      clearTimeout(timeoutId);

      if (result.isComplete && result.wantAdData) {
        // AI generated a complete want ad
        const aiMsg = {
          role: "assistant",
          content: result.aiMessage || "Here's your want ad! I've filled in everything based on what you told me. Take a look and edit anything you'd like.",
          hasPreview: true,
        };
        setMessages((prev) => [...prev, aiMsg]);
        setGeneratedData(result.wantAdData);
        setFairHousingStatus(result.fairHousingStatus || null);
      } else if (result.followUpQuestion) {
        // AI needs more info
        const aiMsg = {
          role: "assistant",
          content: result.followUpQuestion,
        };
        setMessages((prev) => [...prev, aiMsg]);
      } else {
        // Unexpected response — show generic message
        const aiMsg = {
          role: "assistant",
          content: "I'm having trouble processing that. Could you tell me more about what you're looking for? Include the location, budget, and number of bedrooms.",
        };
        setMessages((prev) => [...prev, aiMsg]);
      }
    } catch (err) {
      clearTimeout(timeoutId);
      console.error("AI Chat error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [inputValue, isLoading, messages]);

  const handleRetry = useCallback(() => {
    // Find the last user message and re-send from that point
    const lastUserIdx = messages.map(m => m.role).lastIndexOf("user");
    if (lastUserIdx >= 0) {
      const lastUserMsg = messages[lastUserIdx];
      // Remove messages from the failed user message onward, then re-trigger
      setMessages(prev => prev.slice(0, lastUserIdx));
      setError(null);
      setInputValue(lastUserMsg.content);
    }
  }, [messages]);

  const handleEditReview = useCallback(() => {
    if (generatedData && onWantAdGenerated) {
      onWantAdGenerated(generatedData);
    }
  }, [generatedData, onWantAdGenerated]);

  return (
    <div className="ai-chat-panel">
      <div className="ai-chat-messages">
        {messages.map((msg, idx) => (
          <AiChatMessage key={idx} message={msg}>
            {msg.hasPreview && generatedData && (
              <AiWantAdPreview
                wantAdData={generatedData}
                onEditReview={handleEditReview}
                fairHousingStatus={fairHousingStatus}
              />
            )}
          </AiChatMessage>
        ))}

        {isLoading && (
          <div className="ai-typing-indicator">
            <div className="ai-message-avatar">✨</div>
            <div className="typing-dots">
              <div className="dot" />
              <div className="dot" />
              <div className="dot" />
            </div>
          </div>
        )}

        {error && (
          <div className="ai-chat-error">
            <div className="ai-message-avatar" style={{ background: "#dc2626" }}>!</div>
            <div className="error-bubble">
              {error}
              <br />
              <button className="retry-btn" onClick={handleRetry}>
                Retry
              </button>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <AiChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={handleSend}
        disabled={isLoading}
        placeholder="Describe the property your client wants..."
      />
    </div>
  );
};

export default AiChatPanel;
